<template>
  <div class="bulk-form">
    <div class="bulk-form__header">
      <div>
        <h2 class="bulk-form__title">
          {{ $t('Create Multiple Licenses') }}
        </h2>

        <p class="bulk-form__description">
          {{ $t('Set shared fields once, then fill in the name and type per license.') }}
        </p>
      </div>

      <div class="bulk-form__badge">
        {{ $t('Bulk') }}
      </div>
    </div>

    <form @submit.prevent="submitForm">
      <div class="shared-section">
        <div class="shared-section__label">
          {{ $t('Shared fields') }}
          <span class="shared-section__hint">
            {{ $t('Applied to every row — override per row if needed.') }}
          </span>
        </div>

        <div class="shared-section__grid">
          <div class="form-field form-field--wide">
            <label for="shared-name">
              {{ $t('Name') }}
            </label>
            <input
                id="shared-name"
                v-model.trim="shared.name"
                type="text"
                autocomplete="off"
                :placeholder="$t('Leave empty to set per row')"
                @input="applySharedToAll('name')"
            >
          </div>

          <div class="form-field">
            <label for="shared-start">
              {{ $t('Start Date') }}
              <span class="required">*</span>
            </label>
            <input
                id="shared-start"
                v-model="shared.startDate"
                type="date"
                required
                @change="applySharedToAll('startDate')"
            >
          </div>

          <div class="form-field">
            <label for="shared-exp">
              {{ $t('Expiration Date') }}
              <span class="required">*</span>
            </label>
            <input
                id="shared-exp"
                v-model="shared.expirationDate"
                type="date"
                :min="shared.startDate || null"
                required
                @change="applySharedToAll('expirationDate')"
            >
            <span v-if="sharedDatesInvalid" class="form-field__error">
              {{ $t('Expiration date must be after the start date.') }}
            </span>
          </div>

          <div class="form-field">
            <label for="shared-type">
              {{ $t('Type') }}
            </label>
            <select
                id="shared-type"
                v-model="shared.type"
                @change="applySharedToAll('type')"
            >
              <option value="">{{ $t('— per row —') }}</option>
              <option value="viewer">{{ $t('Viewer') }}</option>
              <option value="editor">{{ $t('Editor') }}</option>
              <option value="manager">{{ $t('Manager') }}</option>
              <option value="admin">{{ $t('Admin') }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- ── Per-license rows ──────────────────────────────────────── -->
      <div class="rows-section">
        <div class="rows-section__label">
          {{ $t('Licenses') }}
          <span class="rows-section__count">{{ rows.length }}</span>
        </div>
        <div class="row-header">
          <span class="row-header__num">#</span>
          <span class="row-header__name">{{ $t('Name') }} <span class="required">*</span></span>
          <span class="row-header__type">{{ $t('Type') }} <span class="required">*</span></span>
          <span class="row-header__user">{{ $t('User') }}</span>
          <span class="row-header__override">{{ $t('Override') }}</span>
          <span class="row-header__remove"></span>
        </div>

        <div
            v-for="(row, index) in rows"
            :key="index"
            class="license-row"
        >
          <span class="license-row__num">{{ index + 1 }}</span>

          <div class="license-row__name">
            <input
                v-model.trim="row.name"
                type="text"
                :required="!shared.name"
                autocomplete="off"
                :placeholder="shared.name ? $t('— use shared —') : $t('License name')"
            >
          </div>

          <div class="license-row__type">
            <select v-model="row.type" :required="!shared.type">
              <option value="">{{ shared.type ? $t('— use shared —') : $t('Type') }}</option>
              <option value="viewer">{{ $t('Viewer') }}</option>
              <option value="editor">{{ $t('Editor') }}</option>
              <option value="manager">{{ $t('Manager') }}</option>
              <option value="admin">{{ $t('Admin') }}</option>
            </select>
          </div>

          <div class="license-row__user">
            <searchable-user-select
                v-model="row.userId"
                :users="users"
                :placeholder="$t('— none —')"
            />
          </div>

          <div class="license-row__overrides">
            <button
                type="button"
                class="override-toggle"
                :class="{ 'override-toggle--active': row._showOverride }"
                :title="$t('Override shared fields for this row')"
                @click="row._showOverride = !row._showOverride"
            >
              ⚙
            </button>

            <div v-if="row._showOverride" class="override-panel">
              <div class="override-field">
                <label>{{ $t('Start') }}</label>
                <input v-model="row.startDate" type="date">
              </div>
              <div class="override-field">
                <label>{{ $t('Expires') }}</label>
                <input v-model="row.expirationDate" type="date" :min="row.startDate || null">
                <span v-if="isExpirationInvalid(row)" class="form-field__error">
                  {{ $t('Invalid dates') }}
                </span>
              </div>
            </div>
          </div>

          <button
              v-if="rows.length > 1"
              type="button"
              class="license-row__remove"
              :title="$t('Remove')"
              @click="removeRow(index)"
          >
            ✕
          </button>
        </div>

        <div class="rows-section__footer">
          <button type="button" class="add-btn" @click="addRow">
            + {{ $t('Add row') }}
          </button>

          <button type="button" class="add-btn add-btn--multi" @click="addMultipleRows">
            + {{ $t('Add') }}
            <input
                v-model.number="quickAddCount"
                type="number"
                min="1"
                max="50"
                class="quick-add-input"
                @click.stop
            >
            {{ $t('rows') }}
          </button>
        </div>
      </div>

      <div class="bulk-form__actions">
        <button
            type="button"
            class="license-button license-button--secondary"
            @click="$emit('abort')"
        >
          {{ $t('Abort') }}
        </button>

        <button
            type="submit"
            class="license-button license-button--primary"
            :disabled="hasErrors"
        >
          {{ $t('Create {n} license(s)', { n: rows.length }) }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import SearchableUserSelect from './SearchableUserSelect.vue';

const createRow = (shared = {}) => ({
  name: shared.name || '',
  type: shared.type || '',
  userId: '',
  startDate: shared.startDate || '',
  expirationDate: shared.expirationDate || '',
  _showOverride: false
});

export default {
  name: 'LicenseBulkForm',

  components: {
    SearchableUserSelect
  },

  props: {
    users: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      shared: {
        name: '',
        startDate: '',
        expirationDate: '',
        type: ''
      },
      rows: [createRow()],
      quickAddCount: 5
    };
  },

  computed: {
    sharedDatesInvalid() {
      if (!this.shared.startDate || !this.shared.expirationDate) {
        return false;
      }

      return this.shared.expirationDate < this.shared.startDate;
    },

    hasErrors() {
      return this.sharedDatesInvalid || this.rows.some(r => this.isExpirationInvalid(r));
    }
  },

  methods: {
    applySharedToAll(field) {
      this.rows.forEach(row => {
        if (!row._showOverride) {
          row[field] = this.shared[field];
        }
      });
    },

    isExpirationInvalid(row) {
      if (!row.startDate || !row.expirationDate) {
        return false;
      }

      return row.expirationDate < row.startDate;
    },

    addRow() {
      this.rows.push(createRow(this.shared));
    },

    addMultipleRows() {
      const count = Math.max(1, Math.min(50, this.quickAddCount || 1));

      for (let i = 0; i < count; i++) {
        this.rows.push(createRow(this.shared));
      }
    },

    removeRow(index) {
      this.rows.splice(index, 1);
    },

    submitForm() {
      if (this.hasErrors) {
        return;
      }

      const licenses = this.rows.map(({ ...row }) => {
        const result = {
          ...row,
          name: row.name || this.shared.name,
          type: row.type || this.shared.type,
          startDate: row.startDate || this.shared.startDate,
          expirationDate: row.expirationDate || this.shared.expirationDate
        };

        if (!result.userId) {
          delete result.userId;
        }

        return result;
      });

      this.$emit('submit', licenses);
    }
  }
};
</script>

<style scoped lang="scss">
.bulk-form {
  max-width: 960px;
  margin: 24px 0;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.bulk-form__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.bulk-form__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.bulk-form__description {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
}

.bulk-form__badge {
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(99, 179, 237, 0.15);
  color: #63b3ed;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Shared fields */

.shared-section {
  margin-bottom: 24px;
  padding: 18px 20px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  border-radius: 10px;
  background: rgba(99, 179, 237, 0.04);
}

.shared-section__label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #63b3ed;
}

.shared-section__hint {
  font-size: 12px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.5);
}

.shared-section__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

/* Rows section */

.rows-section {
  margin-bottom: 24px;
}

.rows-section__label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.rows-section__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 12px;
  font-weight: 700;
}

.row-header {
  display: grid;
  grid-template-columns: 28px 1fr 120px 150px 38px 30px;
  gap: 8px;
  padding: 4px 10px;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.4);
}

.license-row {
  display: grid;
  grid-template-columns: 28px 1fr 120px 150px 38px 30px;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 6px;
  background: rgba(255, 255, 255, 0.02);
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
  }
}

.license-row__num {
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.license-row__remove {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid rgba(235, 87, 87, 0.3);
  border-radius: 5px;
  background: rgba(235, 87, 87, 0.09);
  color: #ff7979;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: rgba(235, 87, 87, 0.2); }
}

/* Override */

.license-row__overrides {
  position: relative;
}

.override-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.8);
  }

  &--active {
    border-color: rgba(99, 179, 237, 0.5);
    color: #63b3ed;
    background: rgba(99, 179, 237, 0.08);
  }
}

.override-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 220px;
  padding: 14px;
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 8px;
  background: #1a2233;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
}

.override-field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

/* Generic form-field */

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;

  label {
    margin-bottom: 7px;
    font-size: 13px;
    font-weight: 600;
  }
}

.form-field--wide {
  grid-column: 1 / -1;
}

/* All inputs & selects */

.form-field input[type="date"],
.form-field input[type="number"],
.form-field input[type="text"],
.form-field select,
.license-row__name input,
.license-row__type select,
.license-row__user select,
.override-field input {
  width: 100%;
  min-height: 36px;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.22);
  color: inherit;
  font: inherit;
  font-size: 13px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.07);
  }
}

.form-field__error {
  margin-top: 5px;
  color: #ff6b6b;
  font-size: 12px;
}

.required { color: #ff6b6b; }


/* Footer */

.rows-section__footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px dashed rgba(255, 255, 255, 0.22);
  border-radius: 7px;
  background: transparent;
  color: rgba(255, 255, 255, 0.62);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.45);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.9);
  }

  &--multi {
    border-color: rgba(99, 179, 237, 0.28);
    color: rgba(99, 179, 237, 0.7);

    &:hover {
      border-color: rgba(99, 179, 237, 0.55);
      background: rgba(99, 179, 237, 0.06);
      color: #63b3ed;
    }
  }
}

.quick-add-input {
  width: 44px;
  min-height: unset !important;
  padding: 2px 4px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.28) !important;
  color: inherit;
  font: inherit;
  font-size: 12px;
  text-align: center;
  box-shadow: none !important;
}

/* Actions */

.bulk-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.license-button {
  min-width: 150px;
  min-height: 42px;
  padding: 9px 18px;
  border: 1px solid transparent;
  border-radius: 7px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}

.license-button--primary {
  background: #ffffff;
  color: #222222;

  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.88); }
}

.license-button--secondary {
  border-color: rgba(255, 255, 255, 0.2);
  background: transparent;
  color: inherit;

  &:hover { background: rgba(255, 255, 255, 0.07); }
}

/* Responsive */

@media (max-width: 860px) {
  .shared-section__grid { grid-template-columns: repeat(2, 1fr); }
  .row-header {
    display: none;
    grid-template-columns: 22px 1fr 30px;
    grid-template-rows: auto auto;
    gap: 6px;
  }

  .license-row__num  { grid-row: 1; grid-column: 1; }
  .license-row__name { grid-row: 1; grid-column: 2; }
  .license-row__remove { grid-row: 1; grid-column: 3; justify-self: end; }
  .license-row__type { grid-row: 2; grid-column: 1 / 2; }
  .license-row__user { grid-row: 2; grid-column: 2; }
  .license-row__overrides { grid-row: 2; grid-column: 3; }
}

@media (max-width: 560px) {
  .bulk-form { padding: 18px; }
  .shared-section__grid { grid-template-columns: 1fr; }
  .bulk-form__actions { flex-direction: column-reverse; }
  .license-button { width: 100%; }
}
</style>

